<div class="main-layout">
  <ocp-sidebar>
    <nav class="main-layout__home-nav">
      <a
        class="main-layout__home-link"
        routerLink="/"
        queryParamsHandling="preserve"
        routerLinkActive="main-layout__home-link--active"
        [routerLinkActiveOptions]="{ exact: true }"
      >
        <ocp-themed-icon name="home.svg" class="main-layout__home-icon" />
        <span>{{ 'nav-tabs.home' | translate }}</span>
      </a>
    </nav>
    <hr class="main-layout__home-separator" />
    @if (contextNavStore.inlineConfig(); as inline) {
      <ocp-sidebar-list
        [items]="inline.items"
        [title]="inline.title"
        [createLabel]="inline.createLabel"
        [variant]="inline.variant"
        [showCreate]="!!inline.showCreate"
        [footerActions]="inline.footerActions ?? []"
        [showCreateInHeader]="!!inline.showCreateInHeader"
        (itemClicked)="onInlineItemClicked($event)"
        (createClicked)="onInlineCreateClicked()"
      />
    } @else {
      @if (!contextNavStore.projectDropdown() && contextNavStore.staticLabel(); as label) {
        <div class="main-layout__static-team">
          <ocp-avatar [userInitials]="label.initial" size="md" singleInitial />
          <span class="main-layout__static-team-name">{{ label.label }}</span>
        </div>
      }
      @if (contextNavStore.projectDropdown(); as projectItem) {
        <ocp-context-nav
          [items]="[projectItem]"
          [showTriggerAvatar]="false"
          (itemSelected)="onProjectDropdownItemSelected($event)"
          (createClicked)="onProjectDropdownCreateClicked()"
        />
      }
      @if (!contextNavStore.staticLabel() && !contextNavStore.projectDropdown()) {
        @if (contextNavStore.items().length) {
          <ocp-context-nav
            [items]="contextNavStore.items()"
            (itemSelected)="onContextNavItemSelected($event)"
            (createClicked)="onContextNavCreateClicked($event)"
            [showCreateInHeader]="false"
          />
        }
        @if (contextNavStore.sidebarMenu().length) {
          <ocp-sidebar-menu
            [items]="contextNavStore.sidebarMenu()"
            [showSeparator]="!!contextNavStore.projectDropdown()"
            (itemClicked)="onSidebarMenuItemClicked($event)"
            (childClicked)="onSidebarMenuChildClicked($event)"
          />
        }
      }
    }
    <ocp-navigation-tabs [excludeHome]="true" />
    <hr class="main-layout__home-separator" />
  </ocp-sidebar>

  <div class="main-layout__body">
    <ocp-header>
      <ocp-breadcrumb />
      <div class="main-layout__header-actions">
        <ocp-language-switcher />
        <ocp-theme-selector />
        <hlm-popover
          #popover="hlmPopover"
          #popoverEl
          align="end"
          [attachTo]="popoverEl"
          class="main-layout__profile-popover"
        >
          <ocp-avatar
            [userInitials]="userInitials()"
            [colorSeed]="userEmail()"
            size="lg"
            class="main-layout__profile-trigger"
            role="button"
            tabindex="0"
            aria-haspopup="menu"
            aria-label="Open profile menu"
            (click)="popover.open()"
            (keydown.enter)="popover.open()"
            (keydown.space)="$event.preventDefault(); popover.open()"
          />
          <hlm-popover-content *hlmPopoverPortal="let ctx" class="w-fit md:p-1">
            <div class="main-layout__popover-content">
              <div class="main-layout__popover-user-info">
                <ocp-avatar [userInitials]="userInitials()" [colorSeed]="userEmail()" size="lg" />
                <div class="main-layout__popover-user-details">
                  @if (userName()) {
                    <p class="main-layout__popover-name">{{ userName() }}</p>
                  }
                  @if (userEmail()) {
                    <p class="main-layout__popover-email">{{ userEmail() }}</p>
                  }
                </div>
              </div>
              <button
                hlmBtn
                variant="ghost"
                class="main-layout__popover-logout-button"
                type="button"
                (click)="onLogoutClick()"
              >
                <span class="main-layout__popover-logout-placeholder"></span>
                {{ 'auth.sign-out' | translate }}
              </button>
              <a
                [href]="helpCenterUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="main-layout__popover-help-center"
              >
                <span class="main-layout__popover-logout-placeholder"></span>
                {{ 'auth.help-center' | translate }}
              </a>
              <div class="main-layout__mobile-sections">
                <hr class="separator" />
                <p class="main-layout__popover-section-label">
                  {{ 'language-switcher.language' | translate }}
                </p>
                @for (lang of languages; track lang.code) {
                  <button
                    hlmBtn
                    variant="ghost"
                    type="button"
                    class="main-layout__mobile-option"
                    [class.main-layout__mobile-option--active]="selectedLangCode() === lang.code"
                    (click)="onMobileLanguageChange(lang)"
                  >
                    @if (selectedLangCode() === lang.code) {
                      <ocp-themed-icon
                        name="checkmark.svg"
                        class="main-layout__mobile-option-check"
                      />
                    } @else {
                      <span class="main-layout__mobile-option-check-placeholder"></span>
                    }
                    <span class="main-layout__mobile-option-name">{{ lang.name }}</span>
                  </button>
                }
                <hr class="separator" />
                <p class="main-layout__popover-section-label">{{ 'theme.theme' | translate }}</p>
                @for (option of themeOptions; track option.mode) {
                  <button
                    hlmBtn
                    variant="ghost"
                    type="button"
                    class="main-layout__mobile-option"
                    [class.main-layout__mobile-option--active]="themeMode() === option.mode"
                    (click)="onMobileThemeChange(option.mode)"
                  >
                    @if (themeMode() === option.mode) {
                      <ocp-themed-icon
                        name="checkmark.svg"
                        class="main-layout__mobile-option-check"
                      />
                    } @else {
                      <span class="main-layout__mobile-option-check-placeholder"></span>
                    }
                    <ocp-themed-icon
                      [name]="option.iconName"
                      class="main-layout__mobile-option-icon"
                    />
                    <span>{{ option.labelKey | translate }}</span>
                  </button>
                }
              </div>
            </div>
          </hlm-popover-content>
        </hlm-popover>
      </div>
    </ocp-header>

    <main class="main-layout__content">
      <ng-content />
    </main>

    <footer class="main-layout__body-footer">
      <a
        class="main-layout__body-footer-link"
        [routerLink]="tocRoute"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ 'sidebar.early-access-program-agreement' | translate }}
      </a>
      <span class="main-layout__body-footer-separator" aria-hidden="true">|</span>
      <a
        class="main-layout__body-footer-link"
        [href]="imprintUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ 'sidebar.imprint' | translate }}
      </a>
    </footer>
  </div>
</div>
